import _toConsumableArray from "@babel/runtime/helpers/toConsumableArray";
import _slicedToArray from "@babel/runtime/helpers/slicedToArray";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
import _defineProperty from "@babel/runtime/helpers/defineProperty";
var _excluded = ["modules"],
    _excluded2 = ["originalIndex"],
    _excluded3 = ["content", "style", "optionOverrides", "childOfPage"],
    _excluded4 = ["originalIndex"],
    _excluded5 = ["styling"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import React from 'react';
import { PaletteProvider } from '@uswitch/ev-components/lib/components/elements/palette';
import merge from 'deepmerge';
import { documentToReactComponents } from '@contentful/rich-text-react-renderer';
import { leftPad } from '@uswitch/ev-utils/lib/string';
import { FIRST_MODULE_ID, MODULE_ID_SEPARATOR, toSiblingMap, __example__devModuleIds as devModuleIds // eslint-disable-line
} from '../utils/sibling-spacing';
import { EVEntry } from './index';
import ModMarker, { displayModMarker } from '../helpers/module-marker';
import { ConditionalWrapper } from '@uswitch/ev-utils/lib/conditional-wrapper';
import { unnest, applyToPairWhen, sequentialGroupBy } from '../utils/arr';
import { contentTypeId } from '@uswitch/ev-utils/lib/contentful';
import SmartContainer from '../helpers/smart-container2';
import DontHydrate, { RedWrapper } from '@uswitch/ev-utils/lib/dont-hydrate';
import { useDoHydrateComponent, useDebugHydration, usePalette, useMeta, useOptions, useIncludes, getEntity } from '@uswitch/ev-utils/lib/context/selectors';
import { UswInfoOutline } from '@uswitch/spark-icons/usw';
import { jsx as _jsx } from "theme-ui/jsx-runtime";
import { createElement as _createElement } from "theme-ui";
import { Fragment as _Fragment } from "theme-ui/jsx-runtime";
import { jsxs as _jsxs } from "theme-ui/jsx-runtime";

var ReactElementOrModule = function ReactElementOrModule(_ref) {
  var item = _ref.item,
      index = _ref.index,
      _ref$moduleIndexPrefi = _ref.moduleIndexPrefix,
      moduleIndexPrefix = _ref$moduleIndexPrefi === void 0 ? '' : _ref$moduleIndexPrefi,
      childOfPage = _ref.childOfPage;
  return /*#__PURE__*/React.isValidElement(item) ? _jsx(item.type, _objectSpread({}, item.props)) : _jsx(EVEntry, _objectSpread(_objectSpread({}, item), {}, {
    moduleIndex: "".concat(moduleIndexPrefix, "-").concat(leftPad(index)),
    track: childOfPage
  }));
};

var PaletteWrapper = function PaletteWrapper(_ref2) {
  var palette = _ref2.palette,
      children = _ref2.children;
  return _jsx(PaletteProvider, {
    value: usePalette(palette),
    children: children
  });
};

var isCriticalImage = function isCriticalImage(_ref3) {
  var selectEntityObj = _ref3.selectEntityObj,
      withinBounds = _ref3.withinBounds,
      content = _ref3.content,
      includes = _ref3.includes;
  var entity = getEntity(selectEntityObj(content), includes);
  return !entity.critical && withinBounds && (contentTypeId(entity) === 'imageModule' || contentTypeId(entity) === 'imageCalloutModule') ? true : entity.critical;
}; // TODO: There is a large amount of logic duplication between Rich &
// Modular pages with the only main difference being the `nodeType`
// data structure, we should try to combine this logic


export var Modular = function Modular(_ref4) {
  var _ref4$modules = _ref4.modules,
      modules = _ref4$modules === void 0 ? [] : _ref4$modules,
      rest = _objectWithoutProperties(_ref4, _excluded);

  var includes = useIncludes();
  var siblingSpacing = useMeta(['siblingSpacing'], []);
  var imageCriticalBound = useMeta(['imageCriticalBound']);
  var siblingSpacingMap = toSiblingMap(siblingSpacing);
  var extractedDevModules = modules.map(function (mod, i) {
    return _objectSpread(_objectSpread({}, mod), {}, {
      originalIndex: i
    });
  }).filter(function (mod) {
    return devModuleIds.indexOf(contentTypeId(getEntity(mod, includes))) > -1;
  });

  var _applyToPairWhen = applyToPairWhen(modules.filter(function (mod) {
    return devModuleIds.indexOf(contentTypeId(getEntity(mod, includes))) === -1;
  }), function (prevMod, currMod, i) {
    var prevModId = i !== 0 ? contentTypeId(getEntity(prevMod, includes)) : FIRST_MODULE_ID;
    var currModId = contentTypeId(getEntity(currMod, includes));
    var modId = "".concat(prevModId).concat(MODULE_ID_SEPARATOR).concat(currModId);
    return siblingSpacingMap[modId];
  }, function (index, mod, spacingConfig) {
    return merge(mod, {
      spacing: spacingConfig.spacing[index] || {}
    });
  }),
      _applyToPairWhen2 = _slicedToArray(_applyToPairWhen, 1),
      spacedSansDevModules = _applyToPairWhen2[0]; // Need to re-inject the dev modules back into the list of spaced modules
  // We loop over each extracted dev module and splice it back into the space modules


  var spacedModules = extractedDevModules.reduce(function (acc, _ref5) {
    var originalIndex = _ref5.originalIndex,
        mod = _objectWithoutProperties(_ref5, _excluded2);

    var pre = acc.slice(0, originalIndex);
    var post = acc.slice(originalIndex);
    return [].concat(_toConsumableArray(pre), [mod], _toConsumableArray(post));
  }, spacedSansDevModules);
  var groupedModules = sequentialGroupBy(spacedModules, function (mod) {
    return contentTypeId(getEntity(mod, includes)) === 'palette';
  });
  return _jsxs(_Fragment, {
    children: [displayModMarker() && _jsx(ModMarker, {
      entity: rest
    }), groupedModules.map(function (_ref6, key) {
      var _ref7 = _slicedToArray(_ref6, 2),
          palette = _ref7[0],
          modules = _ref7[1];

      /** Use palette wrapper if a palette exists, otherwise just children */
      return _jsx(ConditionalWrapper, {
        condition: !!palette,
        wrapper: PaletteWrapper,
        palette: palette,
        children: modules.map(function (entity, key) {
          var isCritical = isCriticalImage({
            selectEntityObj: function selectEntityObj(x) {
              return x;
            },
            withinBounds: imageCriticalBound && Number(imageCriticalBound) ? key <= imageCriticalBound : undefined,
            content: entity,
            includes: includes
          });
          return _createElement(EVEntry, _objectSpread(_objectSpread({}, entity), {}, {
            key: key,
            leIndex: key,
            parentContentType: contentTypeId(rest),
            track: true,
            moduleIndex: "modularpage-".concat(key),
            critical: isCritical
          }));
        })
      }, key);
    })]
  });
};

var modOrContentId = function modOrContentId(entity, includes) {
  var _entity$data;

  return entity.nodeType === 'embedded-entry-block' ? contentTypeId(getEntity(entity === null || entity === void 0 ? void 0 : (_entity$data = entity.data) === null || _entity$data === void 0 ? void 0 : _entity$data.target, includes)) : entity.nodeType;
};

export var RichText = function RichText(_ref8) {
  var _entity$sys;

  var document = _ref8.content,
      _ref8$style = _ref8.style,
      style = _ref8$style === void 0 ? {} : _ref8$style,
      optionOverrides = _ref8.optionOverrides,
      childOfPage = _ref8.childOfPage,
      entity = _objectWithoutProperties(_ref8, _excluded3);

  var includes = useIncludes();
  var options = useOptions();
  var config = {
    optionOverrides: optionOverrides
  };
  var optionsWithOverrides = options(config);
  var siblingSpacing = useMeta(['siblingSpacing'], []);
  var siblingSpacingMap = toSiblingMap(siblingSpacing);
  var imageCriticalBound = useMeta(['imageCriticalBound']);
  var debugHydration = useDebugHydration();
  var doHydrateComponent = useDoHydrateComponent((_entity$sys = entity.sys) === null || _entity$sys === void 0 ? void 0 : _entity$sys.id);
  var doHydrate = !entity.sys || doHydrateComponent;
  if (!document) return _jsx(_Fragment, {}); // For storybook

  if (typeof document === 'string') return _jsx("div", {
    children: document
  });
  var untrimmedContentArr = Array.isArray(document) ? document : Array.isArray(document.content) ? document.content : [];
  var contentArr = untrimmedContentArr.reduce(function (acc, entry) {
    var nodeType = entry.nodeType,
        _entry$content = entry.content,
        content = _entry$content === void 0 ? [] : _entry$content; // When trimming rich content, we should only trim items that are
    // not assets or entries

    if (nodeType === 'embedded-entry-block' || nodeType === 'embedded-asset-block') {
      return acc.concat(entry);
    }

    var trimmedContent = content.filter(function (_ref9) {
      var value = _ref9.value;
      return !/^$/.test(value);
    });
    return trimmedContent.length ? acc.concat(_objectSpread(_objectSpread({}, entry), {}, {
      content: trimmedContent
    })) : acc;
  }, []);
  var extractedDevModules = contentArr.map(function (mod, i) {
    return _objectSpread(_objectSpread({}, mod), {}, {
      originalIndex: i
    });
  }).filter(function (mod) {
    return devModuleIds.indexOf(modOrContentId(mod, includes)) > -1;
  }); // Walk over the modules and apply and sibling spacing that is needed

  var _applyToPairWhen3 = applyToPairWhen(contentArr.filter(function (mod) {
    return devModuleIds.indexOf(modOrContentId(mod, includes)) === -1;
  }), function (prevMod, currMod, i) {
    var prevModId = i > 0 ? modOrContentId(prevMod, includes) : FIRST_MODULE_ID;
    var currModId = modOrContentId(currMod, includes);
    var modId = "".concat(prevModId).concat(MODULE_ID_SEPARATOR).concat(currModId);
    return siblingSpacingMap[modId];
  }, function (index, mod, spacingConfig) {
    return merge(mod, {
      spacing: spacingConfig.spacing[index] || {}
    });
  }),
      _applyToPairWhen4 = _slicedToArray(_applyToPairWhen3, 1),
      spacedSansDevModules = _applyToPairWhen4[0]; // Need to re-inject the dev modules back into the list of spaced modules
  // We loop over each extracted dev module and splice it back into the space modules


  var spacedModules = extractedDevModules.reduce(function (acc, _ref10) {
    var originalIndex = _ref10.originalIndex,
        mod = _objectWithoutProperties(_ref10, _excluded4);

    var pre = acc.slice(0, originalIndex);
    var post = acc.slice(originalIndex);
    return [].concat(_toConsumableArray(pre), [mod], _toConsumableArray(post));
  }, spacedSansDevModules);
  var paletteGroups = sequentialGroupBy(spacedModules, function (mod) {
    return contentTypeId(getEntity(mod.data.target, includes)) === 'palette';
  }, function (mod) {
    return mod.data.target;
  });
  var groupedBlocks = paletteGroups.map(function (_ref11) {
    var _ref12 = _slicedToArray(_ref11, 2),
        palette = _ref12[0],
        content = _ref12[1];

    return [palette, unnest(content, function (_ref13) {
      var nodeType = _ref13.nodeType;
      return nodeType === 'embedded-entry-block';
    })];
  });
  return _jsxs(ConditionalWrapper, {
    condition: !doHydrate,
    wrapper: DontHydrate,
    otherwise: debugHydration ? RedWrapper : undefined,
    children: [displayModMarker() && _jsx(ModMarker, {
      entity: entity
    }), groupedBlocks.map(function (_ref14, i) {
      var _ref15 = _slicedToArray(_ref14, 2),
          palette = _ref15[0],
          groups = _ref15[1];

      return groups.map(function (group, j) {
        var content = documentToReactComponents({
          content: [].concat(group)
        }, optionsWithOverrides);
        var isEmbeddedEntry = group.nodeType === 'embedded-entry-block';
        var spacing = group.spacing || {}; // in order to be able to apply styling between the last and first elements of a smart container, we merge the spacing styling below

        if (Array.isArray(group)) {
          spacing = _objectSpread(_objectSpread({}, group[0].spacing), group[group.length - 1].spacing);
        }

        var key = "rich-pallete_".concat(leftPad(i), "-group_").concat(leftPad(j));
        var parentId = contentTypeId(entity);

        if (isEmbeddedEntry) {
          var isCritical = isCriticalImage({
            selectEntityObj: function selectEntityObj(x) {
              var _x$data;

              return x === null || x === void 0 ? void 0 : (_x$data = x.data) === null || _x$data === void 0 ? void 0 : _x$data.target;
            },
            withinBounds: childOfPage && imageCriticalBound && Number(imageCriticalBound) ? i === 0 && j <= imageCriticalBound : undefined,
            content: group,
            includes: includes
          });
          return _jsx(ConditionalWrapper, {
            condition: !!palette,
            wrapper: PaletteWrapper,
            palette: palette,
            children: _jsx(EVEntry, _objectSpread(_objectSpread({}, content[0]), {}, {
              spacing: spacing,
              parentContentType: parentId,
              moduleIndex: key,
              track: childOfPage,
              critical: isCritical
            }))
          }, key);
        }

        return _jsxs(ConditionalWrapper, {
          condition: !!palette,
          wrapper: PaletteWrapper,
          palette: palette,
          children: [displayModMarker() && _jsx(ModMarker, {
            entity: entity
          }), _jsx(SmartContainer, {
            parentContentType: parentId,
            spacing: spacing,
            styling: entity.styling,
            textContent: true,
            sx: _objectSpread({}, style),
            children: content.map(function (item, index) {
              return _jsx(ReactElementOrModule, {
                index: index,
                item: item,
                moduleIndexPrefix: "key",
                childOfPage: childOfPage
              }, index);
            }, this)
          })]
        }, key);
      });
    })]
  });
};
export var RichPage = function RichPage(props) {
  return RichText(_objectSpread(_objectSpread({}, props), {}, {
    childOfPage: true
  }));
};
export var RichTextBlock = function RichTextBlock(_ref16) {
  var styling = _ref16.styling,
      props = _objectWithoutProperties(_ref16, _excluded5);

  var variant = pickVariant('elements', 'rich-text-block', props.variant);
  return _jsxs(SmartContainer, {
    parentContentType: props.parentContentType,
    variant: variant,
    styling: styling,
    children: [(props === null || props === void 0 ? void 0 : props.variant) === 'disclaimer' ? _jsx(UswInfoOutline, {
      size: 40
    }) : null, _jsx(RichText, _objectSpread({}, props))]
  });
};

var pickVariant = function pickVariant(type, component, variant) {
  var vari = variant === undefined || variant === 'base' ? 'base' : "variants.".concat(variant);
  return "".concat(type, ".").concat(component, ".").concat(vari);
};